import { Carousel } from "@mantine/carousel";
import {
  AspectRatio,
  Badge,
  Box,
  Button,
  Group,
  Image,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { IconBrandWhatsapp, IconCamera, IconZoomIn } from "@tabler/icons-react";
import { useImageLightbox } from "$/components/image-lightbox";
import { Section } from "$/components/section";
import { useInView } from "$/components/use-scroll-progress";
import { WHATSAPP_URL } from "$/data/links";

type Slide = {
  label: string;
  hint: string;
  src?: string;
};

const slides: Slide[] = [
  { label: "Vendas", hint: "PDV com atalhos no teclado", src: "/screenshots/vendas.png" },
  { label: "Balanço", hint: "Caixa do dia, semana e mês", src: "/screenshots/balanco.png" },
  { label: "Catálogo", hint: "Produtos, fotos, estoque", src: "/screenshots/catalogo.png" },
  {
    label: "Movimentações",
    hint: "Registro das movimentações do estoque",
    src: "/screenshots/movimentacoes.png",
  },
  { label: "Relatórios", hint: "Gráficos para você analisar", src: "/screenshots/relatorios.png" },
  { label: "Clientes", hint: "Informações, dívidas e histórico", src: "/screenshots/clientes.png" },
  {
    label: "Fornecedores",
    hint: "Informações e pedidos de compras",
    src: "/screenshots/fornecedores.png",
  },
  { label: "Agenda", hint: "Gerencie seus compromissos por aqui", src: "/screenshots/agenda.png" },
  {
    label: "Comandas",
    hint: "Gerencie suas comandas de forma rápida e eficiente",
    src: "/screenshots/comandas.png",
  },
];

export function Screenshots() {
  const { open: openLightbox } = useImageLightbox();
  const { ref: cameraRef, inView: cameraInView } = useInView<HTMLDivElement>(0.4);

  const handleOpen = (s: Slide) => {
    if (!s.src) return;
    openLightbox({ src: s.src, alt: s.label });
  };

  return (
    <Section bg="gray.0">
      <Stack gap="sm" maw={680} mb={48}>
        <Group align="start">
          <Box
            ref={cameraRef}
            pos="relative"
            w={48}
            h={48}
            mb={4}
            style={{ flexShrink: 0 }}
            aria-hidden
          >
            <ThemeIcon
              size={48}
              radius="md"
              variant="default"
              className={cameraInView ? "camera-shoot" : undefined}
              style={{ position: "relative", zIndex: 1 }}
            >
              <IconCamera size={30} stroke={1.8} />
            </ThemeIcon>
            <Box
              className={cameraInView ? "camera-flash" : undefined}
              pos="absolute"
              top={0}
              left={0}
              w={48}
              h={48}
              style={{
                borderRadius: "var(--mantine-radius-md)",
                background:
                  "radial-gradient(circle, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.6) 60%, rgba(255,255,255,0) 100%)",
                boxShadow: "0 0 24px 8px rgba(255,255,255,0.7)",
                zIndex: 2,
                opacity: 0,
              }}
            />
          </Box>

          <Title
            order={2}
            fz={{ base: 32, md: 48 }}
            fw={800}
            lh={1.08}
            style={{ letterSpacing: "-0.03em" }}
          >
            Como as telas se parecem.
          </Title>
        </Group>

        <Text c="gray.7" fz={{ base: 16, md: 18 }} lh={1.55}>
          Fotos reais de algumas telas do sistema, do jeitinho que você vai ver no dia a dia. Dá uma
          olhada pra ter uma noção de como é simples e direto ao ponto.
        </Text>
      </Stack>

      <Carousel
        slideSize={{ base: "100%", md: "80%" }}
        slideGap="lg"
        withIndicators
        controlSize={48}
        controlsOffset="md"
        emblaOptions={{ align: "center", loop: true }}
      >
        {slides.map((s) => (
          <Carousel.Slide key={s.label}>
            <Box
              className="screenshot-card"
              onClick={() => handleOpen(s)}
              role={s.src ? "button" : undefined}
              tabIndex={s.src ? 0 : undefined}
              onKeyDown={(e) => {
                if (s.src && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  handleOpen(s);
                }
              }}
              style={{
                borderRadius: 20,
                border: "1px solid var(--mantine-color-gray-3)",
                background: "var(--mantine-color-white)",
                overflow: "hidden",
                cursor: s.src ? "pointer" : "default",
                boxShadow: "0 20px 45px -28px rgba(15, 23, 42, 0.45)",
              }}
            >
              <Box pos="relative" className="screenshot-media">
                <AspectRatio ratio={16 / 9} bg="gray.1">
                  {s.src ? (
                    <Image src={s.src} alt={`Tela de ${s.label} do Verdana`} fit="cover" />
                  ) : (
                    <Stack gap={6} align="center" justify="center">
                      <Badge color="orange" variant="light" radius="sm" size="sm">
                        prévia em breve
                      </Badge>
                      <Text fz={{ base: 28, md: 44 }} fw={800} c="dark.9">
                        {s.label}
                      </Text>
                    </Stack>
                  )}
                </AspectRatio>

                {s.src && (
                  <>
                    {/* Backdrop escuro pra destacar o texto sobre a tela */}
                    <Box
                      className="screenshot-overlay"
                      pos="absolute"
                      inset={0}
                      style={{
                        background: "rgba(10, 15, 20, 0.5)",
                        pointerEvents: "none",
                      }}
                    />

                    {/* Legenda centralizada + lupa que aparece no hover */}
                    <Box
                      className="screenshot-caption"
                      pos="absolute"
                      inset={0}
                      style={{ pointerEvents: "none" }}
                    >
                      <Stack gap={6} align="center">
                        <Text
                          className="screenshot-title"
                          fw={800}
                          fz={{ base: 24, md: 34 }}
                          lh={1.15}
                          style={{ letterSpacing: "-0.02em" }}
                        >
                          {s.label}
                        </Text>
                        <Text
                          className="screenshot-hint"
                          fz={{ base: 14, md: 16 }}
                          lh={1.45}
                          maw={420}
                        >
                          {s.hint}
                        </Text>
                        <Group className="screenshot-cta" gap={8} wrap="nowrap" mt="sm">
                          <ThemeIcon size={34} radius="xl" variant="white" c="dark.9">
                            <IconZoomIn size={18} />
                          </ThemeIcon>
                          <Text fz="sm" fw={700} c="white">
                            Ver imagem completa
                          </Text>
                        </Group>
                      </Stack>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          </Carousel.Slide>
        ))}
      </Carousel>

      <Stack mt={40} gap="md" maw={640} mx="auto" align="center">
        <Title order={3} fz={{ base: 22, md: 26 }} fw={700} ta="center" lh={1.2}>
          Quer testar antes de fechar negócio?
        </Title>
        <Text c="gray.7" fz={{ base: 15, md: 16 }} ta="center" lh={1.55}>
          A gente instala o sistema no seu próprio computador e deixa um período pra você usar no
          dia a dia da loja. Se gostar, é só fechar. Se não rolar, sem compromisso e sem pegadinha.
        </Text>
        <Group justify="center" mt={4}>
          <Button
            component="a"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener"
            color="green"
            size="md"
            radius="md"
            leftSection={<IconBrandWhatsapp size={20} />}
          >
            Quero testar no meu computador
          </Button>
        </Group>
      </Stack>
    </Section>
  );
}
